import { ActionTree, GetterTree, Module, MutationTree } from "vuex";
import { IPoolState } from "./types";
import { IIDAStore } from "../types";
import { IPoolPost, IPoolPut, Pool } from "../../models/pools";
import { poolService } from "../../services/Pool.service";
import { Duty } from "../../models/duties";
import { Absence } from "../../models/absences";
import { AbsenceType, eCategory } from "../../models/absenceTypes-model";
import store from "../../store";
import { overlapDay } from "../../utils/dateUtils";
import { WorkHourPoolStatus } from "../../models/workHourPoolStatus";
import { StoreCache, storeCachePeriode } from "../storeCache";

export const getInitialState = (): IPoolState => {
  return {
    searching: false,
    pools: [],
    workHourPoolStatus: new WorkHourPoolStatus(),
    storeCache: new StoreCache(),
    workHourPoolCache: new StoreCache()
  };
};

const namespaced = true;

export const getters: GetterTree<IPoolState, IIDAStore> = {
  GetPools: (state): Pool[] => {
    return state.pools;
  },
  GetPoolsByEmploymentIds:
    (state) =>
    (employmentIds: number[]): Pool[] => {
      const list: Pool[] = state.pools.filter(
        (p) => employmentIds.find((id) => id === p.employment_id) !== undefined
      );
      return list;
    }
};

export const actions: ActionTree<IPoolState, IIDAStore> = {
  reset({ commit }) {
    commit("reset");
  },
  fetchPools({ commit, state }): Promise<Pool[]> {
    return new Promise((resolve, reject) => {
      if (state.pools.length === 0) {
        try {
          poolService.fetchPools().then((pools) => {
            commit("updatePools", pools);
            return resolve(pools);
          });
        } catch (error) {
          return reject(error);
        }
      } else {
        const pools = state.pools;
        return resolve(pools);
      }
    });
  },
  fetchPoolsByEmploymentId({ commit }, employmentId: number): Promise<Pool[]> {
    return new Promise((resolve, reject) => {
      try {
        poolService.fetchPoolsByEmploymentId(employmentId).then((pools) => {
          commit("updatePools", pools);
          return resolve(pools);
        });
      } catch (error) {
        return reject(error);
      }
    });
  },
  createPool({ commit }, pool: Pool): Promise<Pool> {
    return new Promise((resolve, reject) => {
      poolService
        .createPool(pool)
        .then((newPool) => {
          commit("insertPool", newPool);
          resolve(newPool);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  updatePool({ commit }, pool: Pool): Promise<Pool> {
    return new Promise((resolve, reject) => {
      poolService
        .updatePool(pool)
        .then((updatedPool) => {
          commit("updatePool", updatedPool);
          resolve(updatedPool);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  createUpdatePools(
    { commit },
    obj: { pools_to_create: IPoolPost[]; pools_to_update: IPoolPut[] }
  ): Promise<{ createdPools: Pool[]; updatedPools: Pool[]; errors: string[] }> {
    return new Promise((resolve, reject) => {
      poolService
        .createUpdatePools(obj)
        .then((data: { createdPools: Pool[]; updatedPools: Pool[]; errors: string[] }) => {
          if (data.createdPools.length > 0) {
            commit("updatePools", data.createdPools);
          }
          if (data.updatedPools.length > 0) {
            commit("updatePools", data.updatedPools);
          }
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  deletePool({ commit }, poolId: number): Promise<any> {
    return new Promise((resolve, reject) => {
      poolService
        .deletePool(poolId)
        .then((response) => {
          commit("deletePool", poolId);
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  calcRestTime(
    { commit },
    payload: { duties: Duty[]; pools: Pool[]; absences: Absence[] }
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const absencesWithTypes = payload.absences.map((absence: Absence) => {
          const absenceType: AbsenceType | undefined = store.getters["absences/GetAbsenceTypeById"](
            absence.absence_type_id
          );
          return { absence: absence, absencetype: absenceType };
        });
        const absencetypecategory = new Set<number>();
        absencetypecategory.add(eCategory.Vacation);
        absencetypecategory.add(eCategory.Seniority);
        absencetypecategory.add(eCategory.InLieuTime);

        for (let index = 0; index < payload.pools.length; index++) {
          let restInMinutes = 0;
          const pool = payload.pools[index];
          const poolduties = payload.duties.filter((d) => d.work_hour_pool_id === pool.id);
          const poolabsences = absencesWithTypes.filter(
            (d) =>
              d.absence.employee_id === pool.employee_id &&
              d.absencetype?.whole_day &&
              absencetypecategory.has(d.absencetype.category)
          );

          let minutesUsed = 0;
          poolduties.forEach((duty: Duty) => {
            if (!poolabsences.some((a) => overlapDay(a.absence.Periode, duty.start_date))) {
              minutesUsed += duty.duration();
            }
          });
          const poolInMinutes = pool.hours * 60;
          restInMinutes = poolInMinutes - minutesUsed;
          commit("updatePoolUsedTimeAndRestTime", {
            poolId: pool.id,
            usedTime: minutesUsed / 60,
            restTime: restInMinutes / 60
          });
        }

        resolve();
      } catch (error) {
        reject(error);
      }
    });
  },
  getPoolRestTimeByDate({ commit, state }, date: Date): Promise<WorkHourPoolStatus> {
    return new Promise((resolve, reject) => {
      if (!state.workHourPoolCache.isCached({ fra: date, tilOgMed: date })) {
        poolService
          .getPoolStatus(date)
          .then((response: WorkHourPoolStatus) => {
            commit("updatePools", response.work_hour_pools);
            store.dispatch("pools/calcRestTime", {
              duties: response.pool_duties,
              pools: response.work_hour_pools,
              absences: response.absences
            });
            commit("updatePoolStatus", { workHourPoolStatus: response, date: date });

            resolve(response);
          })
          .catch((error) => {
            reject(error);
          });
      } else {
        return resolve(state.workHourPoolStatus);
      }
    });
  },
  getPoolRestTimeByDateEmployeeIdEmploymentId(
    { commit, state },
    query: { date: Date; employeeId: number; employmentId: number }
  ): Promise<WorkHourPoolStatus> {
    return new Promise((resolve, reject) => {
      store
        .dispatch("pools/getPoolRestTimeByDate", query.date)
        .then((response: WorkHourPoolStatus) => {
          const pools = response.work_hour_pools.filter(
            (p) => p.employment_id === query.employmentId
          );
          const duties = response.pool_duties.filter((d) => d.employee_id === query.employeeId);
          const absences = response.absences.filter((a) => a.employee_id === query.employeeId);
          const newResponse = new WorkHourPoolStatus();
          newResponse.work_hour_pools = pools;
          newResponse.pool_duties = duties;
          newResponse.absences = absences;
          newResponse.employments = response.employments;
          resolve(newResponse);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
};

export const mutations: MutationTree<IPoolState> = {
  reset(state: IPoolState) {
    state.storeCache.resetCache();
    Object.assign(state, getInitialState());
  },
  updatePoolUsedTimeAndRestTime(
    state: IPoolState,
    payload: { poolId: number; restTime: number; usedTime: number }
  ) {
    const pool = state.pools.find((p) => p.id === payload.poolId);
    if (pool) {
      pool.usedTime = payload.usedTime;
      pool.restTime = payload.restTime;
    }
  },
  insertPool(state: IPoolState, pool: Pool) {
    state.pools.push(pool);
    state.storeCache.resetCache();
  },
  updatePool(state: IPoolState, pool: Pool) {
    const index = state.pools.findIndex((d) => d.id === pool.id);
    state.pools.splice(index, 1);
    state.pools.push(pool);
    state.storeCache.resetCache();
  },
  deletePool(state: IPoolState, poolId: number) {
    const index = state.pools.findIndex((d) => d.id === poolId);
    state.pools.splice(index, 1);
    state.storeCache.resetCache();
  },
  updatePools(state: IPoolState, pools: Pool[]) {
    pools.forEach((pool: Pool) => {
      const index = state.pools.findIndex((d) => d.id === pool.id);
      if (index > -1) {
        state.pools.splice(index, 1);
        state.pools.push(pool);
      } else {
        state.pools.push(pool);
      }
    });
    state.storeCache.resetCache();
    state.workHourPoolCache.resetCache();
  },
  updatePoolStatus(
    state: IPoolState,
    payload: { workHourPoolStatus: WorkHourPoolStatus; date: Date }
  ) {
    state.workHourPoolStatus = payload.workHourPoolStatus;
    state.workHourPoolCache.addCache(
      new storeCachePeriode({ fra: payload.date, tilOgMed: payload.date })
    );
  }
};

export const poolStoreModule: Module<IPoolState, IIDAStore> = {
  namespaced: namespaced,
  state: getInitialState(),
  getters: getters,
  actions: actions,
  mutations: mutations
};
